/**
 * Sistema di cache unificato per tutti i dealer data
 * Implementa strategie avanzate di caching con TTL intelligente, compression e geolocalizzazione
 */

import { Dealer } from '@/types/dealer';
import { DealerFilterState } from '@/hooks/dealers/types';
import { debugLog, debugError } from '@/utils/debugLogger';
import { isSSR } from '@/lib/isSSR';

// Tipi per il sistema cache unificato
export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
  hits: number;
  size: number;
  compressed: boolean;
  geo?: GeoLocation;
  staleTime: number;
}

export interface GeoLocation {
  lat: number;
  lng: number;
  city?: string;
  region?: string;
}

export interface CacheStats {
  totalEntries: number;
  totalSize: number;
  hitRate: number;
  missRate: number;
  evictionCount: number;
  compressionRatio: number;
  averageResponseTime: number;
}

export interface CacheConfig {
  maxSize: number;
  defaultTTL: number;
  enableCompression: boolean;
  enableGeoCache: boolean;
  enableStaleWhileRevalidate: boolean;
  maxMemoryUsage: number; // bytes
}

export interface IntelligentTTLConfig {
  staticData: number;    // 1 hora para datos estáticos
  liveQuotations: number; // 5-10 minutos para cotizaciones
  userPreferences: number; // 30 minutos
  geoData: number;       // 2 horas para datos geográficos
}

// Configuraciones TTL inteligentes
const INTELLIGENT_TTL: IntelligentTTLConfig = {
  staticData: 60 * 60 * 1000,      // 1 hora
  liveQuotations: 5 * 60 * 1000,   // 5 minutos
  userPreferences: 30 * 60 * 1000, // 30 minutos
  geoData: 2 * 60 * 60 * 1000      // 2 horas
};

class UnifiedDealerCache {
  private memoryCache = new Map<string, CacheEntry>();
  private localStorage?: Storage;
  private compressionEnabled: boolean;
  private stats: CacheStats;
  private config: CacheConfig;
  private backgroundTasks = new Set<string>();

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      maxSize: 100,
      defaultTTL: 20 * 60 * 1000, // 20 minutos
      enableCompression: true,
      enableGeoCache: true,
      enableStaleWhileRevalidate: true,
      maxMemoryUsage: 50 * 1024 * 1024, // 50MB
      ...config
    };

    this.compressionEnabled = this.config.enableCompression && typeof window !== 'undefined';
    this.localStorage = !isSSR() && typeof window !== 'undefined' ? window.localStorage : undefined;
    
    this.stats = {
      totalEntries: 0,
      totalSize: 0,
      hitRate: 0,
      missRate: 0,
      evictionCount: 0,
      compressionRatio: 0,
      averageResponseTime: 0
    };

    this.initializeCache();
  }

  private initializeCache(): void {
    if (!isSSR()) {
      // Limpia cache expirada al inicializar
      this.cleanupExpiredEntries();
      
      // Inicia el cache warming para ciudades populares
      this.warmupPopularCities();
      
      debugLog('cache', 'Unified dealer cache initialized', this.getStats(), 'UnifiedDealerCache');
    }
  }

  /**
   * Genera clave de cache inteligente basada en tipo de datos y geolocalización
   */
  generateKey(
    type: 'dealers' | 'quotations' | 'stats' | 'geo',
    params: {
      metal?: string;
      filters?: DealerFilterState;
      geo?: GeoLocation;
      userId?: string;
      city?: string;
    } = {}
  ): string {
    const { metal, filters, geo, userId, city } = params;
    
    let key = `unified_${type}`;
    
    if (metal) key += `_${metal}`;
    if (city) key += `_${city}`;
    if (userId) key += `_${userId}`;
    
    if (filters) {
      const filterHash = this.hashFilters(filters);
      key += `_${filterHash}`;
    }
    
    if (geo && this.config.enableGeoCache) {
      const geoKey = this.generateGeoKey(geo);
      key += `_${geoKey}`;
    }
    
    return key;
  }

  /**
   * Obtiene datos con estrategia stale-while-revalidate
   */
  async get<T>(
    key: string,
    fetchFn?: () => Promise<T>,
    ttlOverride?: number
  ): Promise<T | null> {
    const startTime = Date.now();
    
    try {
      // Busca en memoria primero
      const memoryCached = this.getFromMemory<T>(key);
      if (memoryCached) {
        this.updateStats('hit', Date.now() - startTime);
        return memoryCached;
      }

      // Busca en localStorage
      const storageCached = this.getFromStorage<T>(key);
      if (storageCached) {
        // Mueve a memoria para acceso más rápido
        this.setInMemory(key, storageCached, ttlOverride);
        this.updateStats('hit', Date.now() - startTime);
        return storageCached;
      }

      // Cache miss - fetch fresh data if function provided
      if (fetchFn) {
        this.updateStats('miss', Date.now() - startTime);
        return this.fetchAndCache(key, fetchFn, ttlOverride);
      }

      this.updateStats('miss', Date.now() - startTime);
      return null;
    } catch (error) {
      debugError('cache', 'Error getting from cache:', error, 'UnifiedDealerCache');
      this.updateStats('miss', Date.now() - startTime);
      return null;
    }
  }

  /**
   * Establece datos en cache con TTL inteligente
   */
  set<T>(
    key: string,
    data: T,
    options: {
      ttl?: number;
      geo?: GeoLocation;
      compress?: boolean;
      priority?: 'high' | 'normal' | 'low';
    } = {}
  ): void {
    const { ttl, geo, compress = this.compressionEnabled, priority = 'normal' } = options;
    
    // Determina TTL inteligente basado en el tipo de datos
    const intelligentTTL = this.getIntelligentTTL(key, ttl);
    
    try {
      // Cache en memoria
      this.setInMemory(key, data, intelligentTTL, geo, compress);
      
      // Cache en localStorage para persistencia
      if (priority !== 'low') {
        this.setInStorage(key, data, intelligentTTL, compress);
      }
      
      debugLog('cache', `Cached data for key: ${key}`, {
        ttl: intelligentTTL,
        compressed: compress,
        size: this.estimateSize(data)
      }, 'UnifiedDealerCache');
    } catch (error) {
      debugError('cache', 'Error setting cache:', error, 'UnifiedDealerCache');
    }
  }

  /**
   * Cache warming proactivo para ciudades populares
   */
  private async warmupPopularCities(): Promise<void> {
    const popularCities = ['Roma', 'Milano', 'Napoli', 'Torino', 'Firenze'];
    
    for (const city of popularCities) {
      const key = this.generateKey('dealers', { metal: 'ORO', city });
      
      if (!this.has(key)) {
        // Schedule background warming
        this.scheduleBackgroundWarm(key, city);
      }
    }
  }

  /**
   * Implementa stale-while-revalidate
   */
  private async fetchAndCache<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    // Check if already fetching in background
    if (this.backgroundTasks.has(key)) {
      // Return stale data if available
      const staleData = this.getStaleData<T>(key);
      if (staleData) {
        return staleData;
      }
    }

    this.backgroundTasks.add(key);
    
    try {
      const data = await fetchFn();
      this.set(key, data, { ttl });
      return data;
    } finally {
      this.backgroundTasks.delete(key);
    }
  }

  private getFromMemory<T>(key: string): T | null {
    const entry = this.memoryCache.get(key);
    if (!entry) return null;

    const now = Date.now();
    
    // Check if expired
    if (now > entry.timestamp + entry.ttl) {
      this.memoryCache.delete(key);
      return null;
    }

    // Check if stale but still valid for stale-while-revalidate
    if (this.config.enableStaleWhileRevalidate && now > entry.timestamp + entry.staleTime) {
      // Schedule background refresh
      this.scheduleBackgroundRefresh(key);
    }

    entry.hits++;
    return this.decompressData(entry.data, entry.compressed);
  }

  private setInMemory<T>(
    key: string,
    data: T,
    ttl: number,
    geo?: GeoLocation,
    compress: boolean = false
  ): void {
    const compressed = compress ? this.compressData(data) : data;
    const size = this.estimateSize(compressed);
    const staleTime = ttl * 0.5; // 50% del TTL para considerar stale

    const entry: CacheEntry<any> = {
      data: compressed,
      timestamp: Date.now(),
      ttl,
      hits: 0,
      size,
      compressed: compress,
      geo,
      staleTime
    };

    // Check memory usage before adding
    if (this.getCurrentMemoryUsage() + size > this.config.maxMemoryUsage) {
      this.evictLeastUsed();
    }

    this.memoryCache.set(key, entry);
    this.updateCacheStats();
  }

  private getFromStorage<T>(key: string): T | null {
    if (!this.localStorage) return null;

    try {
      const stored = this.localStorage.getItem(`unified_cache_${key}`);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      const now = Date.now();

      if (now > parsed.timestamp + parsed.ttl) {
        this.localStorage.removeItem(`unified_cache_${key}`);
        return null;
      }

      return this.decompressData(parsed.data, parsed.compressed);
    } catch (error) {
      debugError('cache', 'Error reading from localStorage:', error, 'UnifiedDealerCache');
      return null;
    }
  }

  private setInStorage<T>(key: string, data: T, ttl: number, compress: boolean): void {
    if (!this.localStorage) return;

    try {
      const compressed = compress ? this.compressData(data) : data;
      
      const entry = {
        data: compressed,
        timestamp: Date.now(),
        ttl,
        compressed: compress
      };

      this.localStorage.setItem(`unified_cache_${key}`, JSON.stringify(entry));
    } catch (error) {
      // Handle localStorage quota exceeded
      if (error.name === 'QuotaExceededError') {
        this.cleanupOldStorageEntries();
        debugLog('cache', 'localStorage quota exceeded, cleaned up old entries', undefined, 'UnifiedDealerCache');
      } else {
        debugError('cache', 'Error writing to localStorage:', error, 'UnifiedDealerCache');
      }
    }
  }

  private getIntelligentTTL(key: string, ttlOverride?: number): number {
    if (ttlOverride) return ttlOverride;

    if (key.includes('quotations')) return INTELLIGENT_TTL.liveQuotations;
    if (key.includes('stats')) return INTELLIGENT_TTL.staticData;
    if (key.includes('geo')) return INTELLIGENT_TTL.geoData;
    if (key.includes('user')) return INTELLIGENT_TTL.userPreferences;

    return this.config.defaultTTL;
  }

  private compressData<T>(data: T): T | string {
    if (!this.compressionEnabled) return data;
    
    try {
      // Encode to Base64 with Unicode support
      const jsonString = JSON.stringify(data);
      // First encode to UTF-8 percent-encoding, then convert to Latin1 for btoa
      return btoa(encodeURIComponent(jsonString).replace(/%([0-9A-F]{2})/g,
        (_, p1) => String.fromCharCode(parseInt(p1, 16))
      ));
    } catch (error) {
      debugError('cache', 'Compression failed, using uncompressed data:', error, 'UnifiedDealerCache');
      return data;
    }
  }

  private decompressData<T>(data: any, compressed: boolean): T {
    if (!compressed || !this.compressionEnabled) return data;
    
    try {
      // Decode Base64 and convert back from Latin1 to UTF-8
      const decoded = atob(data);
      const jsonString = decodeURIComponent(
        decoded.split('').map(c => 
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        ).join('')
      );
      return JSON.parse(jsonString);
    } catch (error) {
      // Fallback: try direct atob for old cached data
      try {
        const fallbackString = atob(data);
        return JSON.parse(fallbackString);
      } catch {
        debugError('cache', 'Decompression failed:', error, 'UnifiedDealerCache');
        return data;
      }
    }
  }

  private estimateSize(data: any): number {
    try {
      return JSON.stringify(data).length * 2; // Rough estimate
    } catch {
      return 1000; // Default size estimate
    }
  }

  private generateGeoKey(geo: GeoLocation): string {
    // Round coordinates to improve cache hit rate
    const lat = Math.round(geo.lat * 100) / 100;
    const lng = Math.round(geo.lng * 100) / 100;
    return `${lat}_${lng}`;
  }

  private hashFilters(filters: DealerFilterState): string {
    const relevant = {
      search: filters.search || '',
      range: filters.range,
      verified: filters.filterVerified,
      activated: filters.filterActivated,
      sort: filters.sortOrder
    };
    
    try {
      const jsonString = JSON.stringify(relevant);
      // Safe Base64 encoding for Unicode
      return btoa(encodeURIComponent(jsonString).replace(/%([0-9A-F]{2})/g,
        (_, p1) => String.fromCharCode(parseInt(p1, 16))
      )).slice(0, 8);
    } catch {
      // Fallback to simple hash if encoding fails
      return this.simpleHash(JSON.stringify(relevant));
    }
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).slice(0, 8);
  }

  private getCurrentMemoryUsage(): number {
    return Array.from(this.memoryCache.values())
      .reduce((total, entry) => total + entry.size, 0);
  }

  private evictLeastUsed(): void {
    const entries = Array.from(this.memoryCache.entries());
    entries.sort(([, a], [, b]) => a.hits - b.hits);
    
    const toEvict = entries.slice(0, Math.ceil(entries.length * 0.2)); // Evict 20%
    
    for (const [key] of toEvict) {
      this.memoryCache.delete(key);
      this.stats.evictionCount++;
    }
  }

  private cleanupExpiredEntries(): void {
    const now = Date.now();
    
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now > entry.timestamp + entry.ttl) {
        this.memoryCache.delete(key);
      }
    }
  }

  private cleanupOldStorageEntries(): void {
    if (!this.localStorage) return;

    const keysToRemove: string[] = [];
    
    for (let i = 0; i < this.localStorage.length; i++) {
      const key = this.localStorage.key(i);
      if (key?.startsWith('unified_cache_')) {
        try {
          const stored = this.localStorage.getItem(key);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Date.now() > parsed.timestamp + parsed.ttl) {
              keysToRemove.push(key);
            }
          }
        } catch {
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => this.localStorage!.removeItem(key));
  }

  private scheduleBackgroundWarm(key: string, city: string): void {
    // Implementation would depend on available data fetching functions
    debugLog('cache', `Scheduled background warming for ${city}`, { key }, 'UnifiedDealerCache');
  }

  private scheduleBackgroundRefresh(key: string): void {
    // Implementation would trigger background data refresh
    debugLog('cache', `Scheduled background refresh for ${key}`, undefined, 'UnifiedDealerCache');
  }

  private getStaleData<T>(key: string): T | null {
    const entry = this.memoryCache.get(key);
    if (!entry) return null;

    return this.decompressData(entry.data, entry.compressed);
  }

  private updateStats(type: 'hit' | 'miss', responseTime: number): void {
    if (type === 'hit') {
      this.stats.hitRate = (this.stats.hitRate + 1) / 2; // Moving average
    } else {
      this.stats.missRate = (this.stats.missRate + 1) / 2;
    }
    
    this.stats.averageResponseTime = (this.stats.averageResponseTime + responseTime) / 2;
  }

  private updateCacheStats(): void {
    this.stats.totalEntries = this.memoryCache.size;
    this.stats.totalSize = this.getCurrentMemoryUsage();
  }

  /**
   * Métodos públicos para gestión de cache
   */
  has(key: string): boolean {
    return this.memoryCache.has(key) || 
           (this.localStorage?.getItem(`unified_cache_${key}`) !== null);
  }

  invalidate(keyPattern?: string): void {
    if (keyPattern) {
      const regex = new RegExp(keyPattern);
      for (const key of this.memoryCache.keys()) {
        if (regex.test(key)) {
          this.memoryCache.delete(key);
        }
      }
      
      // También limpiar localStorage
      if (this.localStorage) {
        for (let i = this.localStorage.length - 1; i >= 0; i--) {
          const key = this.localStorage.key(i);
          if (key?.startsWith('unified_cache_') && regex.test(key)) {
            this.localStorage.removeItem(key);
          }
        }
      }
    } else {
      this.memoryCache.clear();
      if (this.localStorage) {
        for (let i = this.localStorage.length - 1; i >= 0; i--) {
          const key = this.localStorage.key(i);
          if (key?.startsWith('unified_cache_')) {
            this.localStorage.removeItem(key);
          }
        }
      }
    }
    
    this.updateCacheStats();
    debugLog('cache', 'Cache invalidated', { pattern: keyPattern }, 'UnifiedDealerCache');
  }

  getStats(): CacheStats {
    return { ...this.stats };
  }

  // Métodos específicos para dealers
  async getDealers(
    metal: string,
    filters?: DealerFilterState,
    geo?: GeoLocation,
    fetchFn?: () => Promise<Dealer[]>
  ): Promise<Dealer[] | null> {
    const key = this.generateKey('dealers', { metal, filters, geo });
    return this.get<Dealer[]>(key, fetchFn);
  }

  setDealers(
    metal: string,
    dealers: Dealer[],
    filters?: DealerFilterState,
    geo?: GeoLocation
  ): void {
    const key = this.generateKey('dealers', { metal, filters, geo });
    this.set(key, dealers, { geo, priority: 'high' });
  }

  /**
   * Delete specific cache entry
   */
  delete(key: string): void {
    this.memoryCache.delete(key);
    if (this.localStorage) {
      this.localStorage.removeItem(`unified_cache_${key}`);
    }
    this.updateCacheStats();
    debugLog('cache', `Deleted cache entry: ${key}`, undefined, 'UnifiedDealerCache');
  }

  /**
   * Clear all cache entries (alias for invalidate() without pattern)
   */
  clear(): void {
    this.invalidate();
  }

  /**
   * Get timestamp for specific cache entry
   */
  getTimestamp(key: string): number | null {
    const entry = this.memoryCache.get(key);
    if (entry) {
      return entry.timestamp;
    }

    if (this.localStorage) {
      try {
        const stored = this.localStorage.getItem(`unified_cache_${key}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed.timestamp;
        }
      } catch (error) {
        debugError('cache', 'Error reading timestamp from localStorage:', error, 'UnifiedDealerCache');
      }
    }

    return null;
  }
}

// Instancia singleton del cache unificado
export const unifiedDealerCache = new UnifiedDealerCache({
  maxSize: 200,
  defaultTTL: 20 * 60 * 1000, // 20 minutos
  enableCompression: true,
  enableGeoCache: true,
  enableStaleWhileRevalidate: true,
  maxMemoryUsage: 100 * 1024 * 1024 // 100MB
});